import React, { useEffect, useState } from "react";
import axios from "axios";
import "./MembershipHistoryModal.css";
import Select from "react-select";

function MembershipHistoryModal({ memberId, onClose }) {
    const membershipCategoryOptions = [
        { value: "current", label: "Current member" },
        { value: "former", label: "Not a member anymore" },
    ];

    // ───────── state ─────────
    const [history, setHistory]                 = useState([]);
    const [lodges, setLodges]                   = useState([]);
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [membershipStatuses, setMembershipStatuses] = useState([]);
    const [members, setMembers]                 = useState([]);

    const [formData, setFormData] = useState({});
    const [errors,   setErrors]   = useState({});
    const [editingId, setEditingId] = useState(null);

    // ───────── initial load ─────────
    useEffect(() => {
        const loadAll = async () => {
            if (!memberId) return;
            await Promise.all([
                fetchLodges(),
                fetchMembershipTypes(),
                fetchMembershipStatuses(),
                fetchMembers(),
            ]);
            await fetchHistory(); // ensure we do this last
        };
        loadAll();
    }, [memberId]);

    // ───────── helpers ─────────
    const toSASTDate = (d) => {
        if (!d) return "";
        const date = new Date(d);
        return new Date(date.getTime() + 2 * 60 * 60 * 1000).toISOString().split("T")[0];
    };
    const memberLabel = (id) => {
        const m = members.find((x) => x.id === id);
        return m ? `${m.first_name} ${m.last_name}` : "";
    };
    const formatStreamLabel = (value, fallbackText = "") => {
        const label = String(value || "").trim();
        const combined = `${label} ${fallbackText || ""}`.toLowerCase();
        if (/royal\s*arch|chapter|_ra\b/.test(combined)) return "Royal Arch";
        if (/knights?\s*mason|council/.test(combined)) return "Knight Masons";
        if (/templar|preceptory|conclave|malta/.test(combined)) return "Knights Templar";
        if (/craft|lodge/.test(combined)) return "Craft";
        if (!label) return "";
        return label
            .replace(/\bLodges\b/i, "")
            .replace(/\bChapters\b/i, "")
            .replace(/\bCouncils\b/i, "")
            .replace(/\bConclaves\b/i, "")
            .replace(/\bPreceptories\b/i, "")
            .replace(/\(Local(?: level)?\)/i, "")
            .replace(/\s+/g, " ")
            .trim();
    };
    const lodgeStreamLabel = (lodgeId) => {
        const lodge = lodges.find((item) => item.id === lodgeId);
        return formatStreamLabel(lodge?.lodge_type_description, lodge?.name);
    };
    const mainBodyLabel = (lodgeId) => {
        const streamLabel = lodgeStreamLabel(lodgeId);
        return streamLabel ? `Main ${streamLabel} body` : "Main body";
    };

    // ───────── fetchers ─────────
    const fetchHistory = async () => {
        try {
            const { data } = await axios.get(`/api/resources/members/${memberId}/history`);
            setHistory(
                data.map((h) => ({
                    ...h,
                    start_date: toSASTDate(h.start_date),
                    end_date  : h.end_date ? toSASTDate(h.end_date) : "Present",
                }))
            );
        } catch (e) { console.error(e); }
    };
    const fetchLodges    = async () => { try { setLodges((await axios.get("/api/resources/lodges")).data);}                 catch(e){console.error(e);} };
    const fetchMembershipTypes = async () => { try { setMembershipTypes((await axios.get("/api/membership-types")).data);} catch(e){console.error(e);} };
    const fetchMembershipStatuses = async () => { try { setMembershipStatuses((await axios.get("/api/membership-statuses")).data);} catch(e){console.error(e);} };
    const fetchMembers  = async () => { try { setMembers((await axios.get("/api/resources/members")).data);}               catch(e){console.error(e);} };

    // ───────── form logic ─────────
    const handleInputChange = ({ target: { name, value } }) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    };
    const validateForm = () => {
        const req = ["lodge_id"];
        const errs = {};
        req.forEach((f) => !formData[f] && (errs[f] = `${f.replace(/_/g, " ")} is required.`));
        setErrors(errs);
        return !Object.keys(errs).length;
    };
    const clearForm = () => { setFormData({}); setEditingId(null); setErrors({}); };

    const currentMembershipCategory = () => {
        if (formData.membership_category) return formData.membership_category;

        const selectedStatus = membershipStatuses.find((status) => status.id === formData.membership_status_id);
        const statusName = String(selectedStatus?.name || "").toLowerCase();
        if (statusName === "active" && !formData.end_date) return "current";
        if (statusName === "non-active" || formData.end_date) return "former";
        return "";
    };

    const handleMembershipCategoryChange = (option) => {
        setFormData((prev) => ({ ...prev, membership_category: option?.value || "" }));
    };

    const handleSave = async () => {
        if (!validateForm()) { alert("Please fill in all required fields."); return; }
        const payload = {
            ...formData,
            membership_type_id: formData.membership_type_id || null,
            membership_status_id: formData.membership_status_id || null,
            membership_category: formData.membership_category || null,
            is_mother_lodge: Boolean(formData.is_mother_lodge),
            start_date : toSASTDate(formData.start_date) || null,
            end_date   : toSASTDate(formData.end_date) || null,
            proposer_id: formData.proposer_id || null,
            seconder_id: formData.seconder_id || null,
        };
        try {
            if (editingId) {
                await axios.put(`/api/resources/members/${memberId}/history/${editingId}`, payload);
            } else {
                await axios.post(`/api/resources/members/${memberId}/history`, payload);
            }
            fetchHistory();
            clearForm();
            alert("History saved.");
        } catch (e) { console.error(e); alert("Save failed."); }
    };

    const handleEdit = (row) => {
        setEditingId(row.id);
        setFormData({
            lodge_id            : row.lodge_id,
            membership_type_id  : row.membership_type_id,
            membership_status_id: row.membership_status_id,
            membership_category : row.membership_category || "",
            start_date          : row.start_date === "Present" ? "" : row.start_date,
            end_date            : row.end_date === "Present" ? "" : row.end_date,
            comments            : row.comments || "",
            proposer_id         : row.proposer_id || "",
            seconder_id         : row.seconder_id || "",
            is_mother_lodge     : Boolean(row.is_mother_lodge),
        });
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this record?")) return;
        try {
            await axios.delete(`/api/resources/members/${memberId}/history/${id}`);
            fetchHistory();
        } catch (e) { console.error(e); alert("Delete failed."); }
    };

    // ───────── render ─────────
    return (
        <div className="membership-history-modal-overlay">
            <div className="membership-history-modal-content">
                <button className="membership-history-modal-close-btn" onClick={() => { clearForm(); onClose(); }}>&times;</button>
                <h2 className="membership-history-modal-title">Membership History</h2>

                <div className="membership-history-scroll-container">
                    {/* Saved history list (like Degrees modal) */}
                    <div className="membership-history-form-group">
                        <label className="membership-degrees-label">Saved History</label>
                        <ul className="membership-history-list">
                            {history.map((item) => (
                                <li key={item.id} className={`membership-history-item ${item.is_mother_lodge ? "main-lodge" : ""}`}>
                                    <p>
                                        {lodges.find((l) => l.id === item.lodge_id)?.name || "Unknown Lodge"} —{" "}
                                        {membershipTypes.find((t) => t.id === item.membership_type_id)?.name || "Type not recorded"} (
                                        {item.start_date || "Start not recorded"} to {item.end_date})
                                        {item.is_mother_lodge ? ` · ${mainBodyLabel(item.lodge_id)}` : ""}
                                    </p>
                                    {(item.proposer_id || item.seconder_id) && (
                                        <p className="membership-history-prop-sec">
                                            {item.proposer_id && <>Proposed: {memberLabel(item.proposer_id)} </>}
                                            {item.seconder_id && <>| Seconded: {memberLabel(item.seconder_id)}</>}
                                        </p>
                                    )}
                                    <div className="membership-history-buttons">
                                        <button className="membership-history-edit-btn"   onClick={() => handleEdit(item)}>Edit</button>
                                        <button className="membership-history-delete-btn" onClick={() => handleDelete(item.id)}>Delete</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <button onClick={clearForm} className="membership-history-clear-btn">Add New History</button>

                    {/* Form Section */}
                    <div className="membership-history-form">
                        <div className="membership-history-form-group membership-history-category-helper">
                            <label className="membership-history-label">Membership Category</label>
                            <Select
                                options={membershipCategoryOptions}
                                onChange={handleMembershipCategoryChange}
                                value={membershipCategoryOptions.find((option) => option.value === currentMembershipCategory()) || null}
                                isClearable
                                placeholder="Choose current or former member"
                                className="membership-history-select"
                            />
                            <p className="membership-history-help">
                                Tracks whether this lodge membership is current; type, status, and dates stay separate.
                            </p>
                        </div>

                        {/* Lodge dropdown */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">Lodge</label>
                            <Select
                                options={lodges.map((l) => ({ value: l.id, label: l.name }))}
                                onChange={(opt) => handleInputChange({ target: { name: "lodge_id", value: opt ? opt.value : "" } })}
                                value={lodges.find((l) => l.id === formData.lodge_id) ? ({
                                    value: formData.lodge_id,
                                    label: lodges.find((l) => l.id === formData.lodge_id).name,
                                }) : null}
                                isSearchable
                                isClearable
                                placeholder="Select Lodge"
                                className={`membership-history-select ${errors.lodge_id ? "membership-history-error-field" : ""}`}
                            />
                            {errors.lodge_id && <p className="membership-history-error">{errors.lodge_id}</p>}
                        </div>

                        <label className="membership-history-main-lodge">
                            <input
                                type="checkbox"
                                checked={Boolean(formData.is_mother_lodge)}
                                onChange={(event) => handleInputChange({ target: { name: "is_mother_lodge", value: event.target.checked } })}
                            />
                            <span>
                                <strong>{mainBodyLabel(formData.lodge_id)}</strong>
                                Mark this as the member's primary current body for this stream. Selecting it clears the main flag only from memberships in the same stream.
                            </span>
                        </label>

                        {/* Membership Type dropdown */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">Membership Type</label>
                            <Select
                                options={membershipTypes.map((t) => ({ value: t.id, label: t.name }))}
                                onChange={(opt) => handleInputChange({ target: { name: "membership_type_id", value: opt ? opt.value : "" } })}
                                value={membershipTypes.find((t) => t.id === formData.membership_type_id) ? ({
                                    value: formData.membership_type_id,
                                    label: membershipTypes.find((t) => t.id === formData.membership_type_id).name,
                                }) : null}
                                isSearchable
                                isClearable
                                placeholder="Select Membership Type"
                                className={`membership-history-select ${errors.membership_type_id ? "membership-history-error-field" : ""}`}
                            />
                            {errors.membership_type_id && <p className="membership-history-error">{errors.membership_type_id}</p>}
                        </div>

                        {/* Membership Status dropdown */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">Status</label>
                            <Select
                                options={membershipStatuses.map((s) => ({ value: s.id, label: s.name }))}
                                onChange={(opt) => handleInputChange({ target: { name: "membership_status_id", value: opt ? opt.value : "" } })}
                                value={membershipStatuses.find((s) => s.id === formData.membership_status_id) ? ({
                                    value: formData.membership_status_id,
                                    label: membershipStatuses.find((s) => s.id === formData.membership_status_id).name,
                                }) : null}
                                isSearchable
                                isClearable
                                placeholder="Select Membership Status"
                                className={`membership-history-select ${errors.membership_status_id ? "membership-history-error-field" : ""}`}
                            />
                            {errors.membership_status_id && <p className="membership-history-error">{errors.membership_status_id}</p>}
                        </div>

                        {/* Proposer dropdown */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">Proposed By</label>
                            <Select
                                options={members.map((m) => ({ value: m.id, label: `${m.first_name} ${m.last_name}` }))}
                                onChange={(opt) => handleInputChange({ target: { name: "proposer_id", value: opt ? opt.value : "" } })}
                                value={members.find((m) => m.id === formData.proposer_id) ? ({
                                    value: formData.proposer_id,
                                    label: memberLabel(formData.proposer_id),
                                }) : null}
                                isSearchable
                                isClearable
                                placeholder="Select Proposer"
                                className="membership-history-select"
                            />
                        </div>

                        {/* Seconder dropdown */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">Seconded By</label>
                            <Select
                                options={members.map((m) => ({ value: m.id, label: `${m.first_name} ${m.last_name}` }))}
                                onChange={(opt) => handleInputChange({ target: { name: "seconder_id", value: opt ? opt.value : "" } })}
                                value={members.find((m) => m.id === formData.seconder_id) ? ({
                                    value: formData.seconder_id,
                                    label: memberLabel(formData.seconder_id),
                                }) : null}
                                isSearchable
                                isClearable
                                placeholder="Select Seconder"
                                className="membership-history-select"
                            />
                        </div>

                        {/* Start Date input */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">Start Date</label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date || ""}
                                onChange={handleInputChange}
                                className={`membership-history-input ${errors.start_date ? "membership-history-error-field" : ""}`}
                            />
                            {errors.start_date && <p className="membership-history-error">{errors.start_date}</p>}
                        </div>

                        {/* End Date input */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">End Date</label>
                            <input
                                type="date"
                                name="end_date"
                                value={formData.end_date || ""}
                                onChange={handleInputChange}
                                className="membership-history-input"
                            />
                        </div>

                        {/* Comments textarea */}
                        <div className="membership-history-form-group">
                            <label className="membership-history-label">Comments</label>
                            <textarea
                                name="comments"
                                value={formData.comments || ""}
                                onChange={handleInputChange}
                                className="membership-history-textarea"
                            />
                        </div>

                        {/* Save button */}
                        <button onClick={handleSave} className="membership-history-save-btn">
                            {editingId ? "Update" : "Add New"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MembershipHistoryModal;
